export const ROLES = ["admin", "legal_advisor", "user"] as const;
export type Role = (typeof ROLES)[number];

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/** Server is source of truth; UI mirror. Admin and legal_advisor may write. */
export function canEdit(role: Role | null | undefined): boolean {
  return role === "admin" || role === "legal_advisor";
}

/** /admin page access: admin only. */
export function canSeeAdmin(role: Role | null | undefined): boolean {
  return role === "admin";
}

/**
 * /audit-log access: all authenticated users (admin, legal_advisor, and user).
 * History is visible to everyone — Members already have full read access to the
 * same entity data, so showing its change trail exposes nothing new.
 * Authentication (non-null role) is still required; anonymous → false.
 */
export function canSeeAuditLog(role: Role | null | undefined): boolean {
  return role === "admin" || role === "legal_advisor" || role === "user";
}

/** /admin/deleted-entities access: admin only. */
export function canSeeDeletedEntities(role: Role | null | undefined): boolean {
  return role === "admin";
}

/** /regulatory-watch manage (confirm/apply/dismiss/source-edit): admin + legal_advisor. */
export function canManageRegulatory(role: Role | null | undefined): boolean {
  return role === "admin" || role === "legal_advisor";
}

/** All authenticated roles may download entity exports. */
export function canDownload(role: Role | null | undefined): boolean {
  return role === "admin" || role === "legal_advisor" || role === "user";
}

export const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  legal_advisor: "Legal advisor",
  user: "Member",
};
